import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import Table from "$components/table";
import { initiativesApi } from "../api";
import { setShortlistCount } from "$stores/initiative-details";
import { buildDefaultColumns } from "./libs/shortlist-columns";
import OpportunityPanel from "./OpportunityPanel";
import SendPartnerInline from "./SendPartnerInline";

const mapToTableData = (r) => ({ key: r.projectKey, ...r });

const ShortlistTab = () => {
    const { id: initiativeId } = useParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ decisionStatus: "asc" });
    const [selectedOpp, setSelectedOpp] = useState(null);
    const [showSendPartner, setShowSendPartner] = useState(false);
    const [lastSentTo, setLastSentTo] = useState(null);

    useEffect(() => {
        const controller = new AbortController();

        initiativesApi
            .getShortlist(initiativeId)
            .then((res) => {
                if (controller.signal.aborted) return;
                const data = res || [];
                setRows(data);
                setShortlistCount(data.length);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [initiativeId]);

    const handleDecision = useCallback((projectKey, status, note) => {
        setRows((prev) =>
            prev.map((r) =>
                r.projectKey === projectKey
                    ? { ...r, decisionStatus: status, decisionNote: note }
                    : r
            )
        );
    }, []);

    const handleFlag = useCallback((projectKey) => {
        setRows((prev) =>
            prev.map((r) =>
                r.projectKey === projectKey ? { ...r, userFlagged: true } : r
            )
        );
    }, []);

    const rowClick = useCallback((row) => {
        setSelectedOpp(row);
    }, []);

    const columns = useMemo(
        () => buildDefaultColumns({ initiativeId, onDecision: handleDecision }),
        [initiativeId, handleDecision]
    );

    const data = useMemo(() => rows.map(mapToTableData), [rows]);

    if (loading) return <div className="loading">Loading shortlist...</div>;

    return (
        <div className="shortlist-tab">
            {data.length === 0 ? (
                <p className="empty-state">
                    No opportunities shortlisted yet. Use the Curate tab to add some.
                </p>
            ) : (
                <Table
                    allowFilter={false}
                    columns={columns}
                    data={data}
                    hiddenColumns={[]}
                    isLoading={false}
                    onRowClick={rowClick}
                    showPagination={false}
                    sort={sort}
                    setSort={setSort}
                />
            )}
            <div className="shortlist-tab__partner-action">
                {lastSentTo && (
                    <span className="txt -small -light">✓ Review link sent to {lastSentTo}</span>
                )}
                <button
                    className="button -secondary -small"
                    onClick={() => setShowSendPartner(true)}
                    disabled={showSendPartner}
                >
                    Send Partner Review Link
                </button>
                {showSendPartner && (
                    <SendPartnerInline
                        initiativeId={initiativeId}
                        onClose={() => setShowSendPartner(false)}
                        onSent={(name) => {
                            setLastSentTo(name);
                            setShowSendPartner(false);
                        }}
                    />
                )}
            </div>
            <OpportunityPanel
                opportunity={selectedOpp}
                onClose={() => setSelectedOpp(null)}
                onFlagged={handleFlag}
            />
        </div>
    );
};

export default ShortlistTab;
